/**
 * Phase 220 — Self-Service In-App Walkthrough Player
 * Phase 220.b — Contextual: accepts a `placement` prop ('dashboard' |
 * 'help_center' | 'my_portal') so the same component can show different
 * tutorials depending on WHERE it's mounted.
 *
 * Phase 220.c — Dashboard-only lifecycle gates (handled server-side; frontend
 * just reflects the empty `entries` it gets back):
 *   • 5-day auto-hide from `user.created_at` (server returns 0 entries)
 *   • Per-user permanent dismiss button — POSTs `/me/dashboard/dismiss-walkthrough`,
 *     then triggers a global `outplay-user-refresh` so AuthContext re-pulls
 *     /auth/me with the new flag. Cross-device because it lives on db.users.
 *
 * Help Center / my_portal placements: no dismiss UX, no auto-hide.
 */
import { useState, useEffect, useRef } from 'react';
import { Play, Monitor, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

function safeHostname(rawUrl) {
  try { return new URL(rawUrl).hostname.replace(/^www\./, ''); }
  catch { return ''; }
}

function daysRemainingFromSignup(createdAtIso) {
  if (!createdAtIso) return null;
  try {
    const created = new Date(createdAtIso).getTime();
    const age = (Date.now() - created) / 86400000;
    return Math.max(0, Math.ceil(5 - age));
  } catch { return null; }
}

export default function SelfServiceWalkthroughSection({ className = '', placement = 'dashboard' }) {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  // Dashboard placement uses server-side dismissal (Phase 220.c). Other placements
  // keep the per-session session-storage dismiss for lightweight close-this-tab UX.
  const sessionDismissKey = `walkthrough_dismissed_${placement}`;
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const mountedRef = useRef(true);

  const isDashboardPlacement = placement === 'dashboard';

  useEffect(() => {
    mountedRef.current = true;
    const t = setTimeout(() => {
      // Help Center / my_portal: respect per-session dismiss key
      if (!isDashboardPlacement && typeof window !== 'undefined' &&
          window.sessionStorage?.getItem(sessionDismissKey) === '1') {
        if (mountedRef.current) setSessionDismissed(true);
        if (mountedRef.current) setLoading(false);
        return;
      }
      api.get('/dashboard/walkthroughs', { params: { placement } })
        .then(r => {
          if (!mountedRef.current) return;
          const list = r.data?.entries || [];
          setVideos(list);
          if (list.length > 0) setActiveId(list[0].id);
        })
        .catch(() => {})
        .finally(() => { if (mountedRef.current) setLoading(false); });
    }, 0);
    return () => { mountedRef.current = false; clearTimeout(t); };
  }, [placement, sessionDismissKey, isDashboardPlacement, user?.dashboard_walkthrough_dismissed_at]);

  if (loading || sessionDismissed || videos.length === 0) return null;
  const active = videos.find(v => v.id === activeId) || videos[0];
  if (!active) return null;

  const handleDismiss = async () => {
    // Help Center / my_portal: keep current session-only dismiss UX
    if (!isDashboardPlacement) {
      setSessionDismissed(true);
      try { window.sessionStorage?.setItem(sessionDismissKey, '1'); } catch { /* noop */ }
      return;
    }
    // Phase 220.c — Dashboard placement: permanent per-user dismiss via API
    if (dismissing) return;
    setDismissing(true);
    try {
      await api.post('/me/dashboard/dismiss-walkthrough');
      // Refresh AuthContext so user.dashboard_walkthrough_dismissed_at is now set
      window.dispatchEvent(new CustomEvent('outplay-user-refresh'));
      // Hide immediately on this device — refresh will keep it hidden on every device
      setVideos([]);
      toast.success('Walkthrough hidden — you can always re-watch it from the Help Center');
    } catch {
      toast.error('Could not dismiss — please try again');
    } finally {
      setDismissing(false);
    }
  };

  const daysLeft = isDashboardPlacement ? daysRemainingFromSignup(user?.created_at) : null;

  return (
    <div
      data-testid="self-service-walkthrough-section"
      className={`bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm space-y-5 ${className}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
            <Monitor className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
              Self-Service Guide Hub
              <span className="hidden sm:inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full">
                <Sparkles className="h-2.5 w-2.5" /> No demos needed
              </span>
            </h2>
            <p className="text-xs text-slate-500 leading-snug mt-0.5">
              Quick walkthroughs covering the workflows you&apos;ll use most.
              {isDashboardPlacement && daysLeft !== null && daysLeft > 0 && (
                <span className="block mt-0.5 text-[10px] text-indigo-600 font-semibold">
                  Auto-hides after {daysLeft} {daysLeft === 1 ? 'day' : 'days'} · always available in Help Center
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={dismissing}
          aria-label={isDashboardPlacement ? 'Hide walkthrough permanently from dashboard' : 'Hide walkthrough hub'}
          title={isDashboardPlacement ? "Hide forever — you'll still find these in the Help Center" : 'Hide for this session'}
          data-testid="walkthrough-dismiss-btn"
          className="text-slate-400 hover:text-slate-700 transition-colors p-1 rounded-md hover:bg-slate-100 shrink-0 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Hero video */}
        <div className="lg:col-span-2 space-y-3">
          <div className="w-full aspect-video rounded-xl overflow-hidden border border-slate-200 shadow-inner bg-slate-950">
            <iframe
              key={active.id}
              src={active.embed_url}
              title={active.title}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              className="w-full h-full"
              data-testid="walkthrough-active-iframe"
            />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 leading-tight" data-testid="walkthrough-active-title">
              {active.title}
            </h3>
            {active.description && (
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">{active.description}</p>
            )}
          </div>
        </div>

        {/* Playlist */}
        <div className="space-y-2">
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 block">
            Tutorials ({videos.length})
          </span>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="walkthrough-playlist">
            {videos.map((v) => {
              const isActive = v.id === active.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setActiveId(v.id)}
                  data-testid={`walkthrough-playlist-item-${v.id}`}
                  className={`w-full text-left p-3 border rounded-xl transition-all flex items-start gap-3 ${
                    isActive
                      ? 'bg-slate-900 border-slate-900 text-white shadow-sm'
                      : 'bg-slate-50/70 hover:bg-slate-100 border-slate-100 text-slate-700'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg mt-0.5 shrink-0 ${
                    isActive ? 'bg-indigo-500 text-white' : 'bg-white border border-slate-200 text-slate-500'
                  }`}>
                    <Play className="h-3 w-3" fill="currentColor" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`font-bold text-xs leading-snug ${isActive ? 'text-white' : 'text-slate-900'}`}>
                      {v.title}
                    </p>
                    <p className={`text-[10px] truncate mt-0.5 ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>
                      {safeHostname(v.raw_url) || 'video'}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
